import { useState, useEffect, useCallback } from "react";
import { Bell, X, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import axios from "axios";
import API from "@/apiConfig";

/**
 * NotificationBadge - Bell icon with unread count badge
 * 
 * Shows:
 * - Bell icon with red dot when unread > 0
 * - Dropdown with recent notifications
 * - Mark as read functionality
 */
export function NotificationBadge({ isSignedIn }) {
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchCount = useCallback(async () => {
    if (!isSignedIn) return;
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setCount(res.data.count || 0);
    } catch {
      // Silent fail
    }
  }, [isSignedIn]);

  const fetchNotifications = useCallback(async () => {
    if (!isSignedIn) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data || []);
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  useEffect(() => {
    fetchCount();
    // Poll every 60 seconds
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  const markAsRead = async (notifId) => {
    try {
      await axios.post(`${API}/notifications/${notifId}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silent fail
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setCount(0);
    } catch {
      // Silent fail
    }
  };

  if (!isSignedIn) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5 text-gray-600" />
          {count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
              data-testid="notification-count"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 mr-2"
        align="end"
        data-testid="notification-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold text-navy">Notifications</h3>
          {count > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-gold hover:text-gold/80 flex items-center gap-1"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </button>
          )}
        </div>

        {/* Notifications List */}
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-sm text-gray-400">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="h-8 w-8 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No notifications yet</p>
              <p className="text-xs text-gray-300 mt-1">The Spirit is quiet for now</p>
            </div>
          ) : (
            notifications.slice(0, 10).map((notif) => (
              <NotificationItem
                key={notif.id}
                notif={notif}
                onMarkRead={() => markAsRead(notif.id)}
              />
            ))
          )}
        </div>

        {notifications.length > 10 && (
          <div className="border-t px-4 py-2 text-center">
            <span className="text-xs text-gray-400">
              {notifications.length - 10} more notifications
            </span>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function NotificationItem({ notif, onMarkRead }) {
  const timeAgo = getTimeAgo(notif.created_at);
  const typeIcon = getTypeIcon(notif.type);

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 border-b last:border-0 transition-colors ${
        notif.read ? "bg-white" : "bg-gold/5"
      }`}
      data-testid={`notification-${notif.id}`}
    >
      <div className={`mt-0.5 ${notif.read ? "text-gray-300" : "text-gold"}`}>
        {typeIcon}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${notif.read ? "text-gray-500" : "text-navy"}`}>
          {notif.title}
        </p>
        <p className={`text-xs mt-0.5 ${notif.read ? "text-gray-400" : "text-gray-600"}`}>
          {notif.message}
        </p>
        <p className="text-[10px] text-gray-300 mt-1">{timeAgo}</p>
      </div>
      {!notif.read && (
        <button
          onClick={onMarkRead}
          className="mt-1 p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
          title="Mark as read"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function getTypeIcon(type) {
  // All use Bell for now, but can be customized
  return <Bell className="h-4 w-4" />;
}

function getTimeAgo(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
